import { type RefObject, useEffect, useRef, useState } from 'react';

export interface PopupOpenState {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Attach to the element holding both the popup's trigger and the popup. */
  hostRef: RefObject<HTMLDivElement | null>;
}

/**
 * The open state of every popup in the editor (the pick lists, the + menu,
 * the view widget's cards): a press anywhere outside the host closes it, and
 * the press still does what it would have. The document is listened to, not
 * covered with a fixed backdrop, because an ancestor's blur filter (the view
 * widget's) would confine a fixed backdrop to that ancestor.
 */
export function usePopupOpenState(): PopupOpenState {
  const [open, setOpen] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePress = (event: Event) => {
      if (!hostRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsidePress);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePress);
  }, [open]);
  return { open, setOpen, hostRef };
}
